import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Phone, Mail, MapPin, AlertCircle, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { toast } from './Toast';
import { getApiUrl } from '../config/api';
import './Profile.css';

const Profile = ({ user }) => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');

  useEffect(() => {
    if (user) {
      fetchUserBookings();
    }
  }, [user]);

  const fetchUserBookings = async () => {
    if (!user) return;
    
    console.log('=== FETCHING USER BOOKINGS ===');
    console.log('Current user:', user);
    console.log('User email:', user.email);
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('JWT Token exists:', !!token);
      
      const response = await fetch(getApiUrl('/api/bookings'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('API Response status:', response.status);
      console.log('API Response ok:', response.ok);

      if (response.ok) {
        const userBookings = await response.json();
        console.log('Fetched bookings:', userBookings);
        console.log('Number of bookings:', userBookings.length);
        setBookings(userBookings);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch user bookings:', response.status, errorText);
        toast.error('Failed to load your bookings');
      }
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      toast.error('Error loading bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return <CheckCircle className="status-icon confirmed" size={20} />;
      case 'pending':
        return <Clock className="status-icon pending" size={20} />;
      case 'cancelled':
        return <XCircle className="status-icon cancelled" size={20} />;
      default:
        return <HelpCircle className="status-icon unknown" size={20} />;
    }
  };

  const getStatusClass = (status) => {
    return `booking-status ${status?.toLowerCase() || 'unknown'}`;
  };

  const calculateNights = (checkIn, checkOut) => {
    try {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return 0;
    }
  };

  const groupBookingsByStatus = () => {
    const grouped = {
      upcoming: [],
      completed: [],
      cancelled: [],
      pending: []
    };

    bookings.forEach(booking => {
      const today = new Date();
      const checkOutDate = new Date(booking.checkOutDate);
      
      if (booking.status?.toLowerCase() === 'cancelled') {
        grouped.cancelled.push(booking);
      } else if (booking.status?.toLowerCase() === 'pending') {
        grouped.pending.push(booking);
      } else if (checkOutDate < today) {
        grouped.completed.push(booking);
      } else {
        grouped.upcoming.push(booking);
      }
    });

    return grouped;
  };

  const groupedBookings = groupBookingsByStatus();

  if (!user) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="profile-error">
            <AlertCircle size={48} />
            <h2>Access Denied</h2>
            <p>Please log in to view your profile and bookings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-header">
          <div className="profile-info">
            <h1>Welcome, {user.name}!</h1>
            <p className="user-email">{user.email}</p>
            {user.phone && <p className="user-phone">{user.phone}</p>}
          </div>
          <div className="profile-stats">
            <div className="stat-card">
              <span className="stat-number">{bookings.length}</span>
              <span className="stat-label">Total Bookings</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{groupedBookings.upcoming.length}</span>
              <span className="stat-label">Upcoming Stays</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{groupedBookings.pending.length}</span>
              <span className="stat-label">Pending Approval</span>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-tabs">
            <button 
              className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`}
              onClick={() => setActiveTab('bookings')}
            >
              My Bookings
            </button>
            <button 
              className={`tab-button ${activeTab === 'help' ? 'active' : ''}`}
              onClick={() => setActiveTab('help')}
            >
              Help & Support
            </button>
          </div>

          {activeTab === 'bookings' && (
            <div className="bookings-section">
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading your bookings...</p>
                </div>
              ) : bookings.length === 0 ? (
                <div className="empty-state">
                  <Calendar size={64} />
                  <h3>No Bookings Yet</h3>
                  <p>You haven't made any bookings yet. Explore our rooms and book your perfect stay!</p>
                  <a href="/rooms" className="btn btn-primary">Browse Rooms</a>
                </div>
              ) : (
                <div className="bookings-content">
                  {/* Pending Bookings */}
                  {groupedBookings.pending.length > 0 && (
                    <div className="booking-group">
                      <h3 className="group-title">Pending Approval ({groupedBookings.pending.length})</h3>
                      <div className="bookings-grid">
                        {groupedBookings.pending.map(booking => (
                          <BookingCard key={booking.id} booking={booking} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upcoming Bookings */}
                  {groupedBookings.upcoming.length > 0 && (
                    <div className="booking-group">
                      <h3 className="group-title">Upcoming Stays ({groupedBookings.upcoming.length})</h3>
                      <div className="bookings-grid">
                        {groupedBookings.upcoming.map(booking => (
                          <BookingCard key={booking.id} booking={booking} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completed Bookings */}
                  {groupedBookings.completed.length > 0 && (
                    <div className="booking-group">
                      <h3 className="group-title">Past Stays ({groupedBookings.completed.length})</h3>
                      <div className="bookings-grid">
                        {groupedBookings.completed.map(booking => (
                          <BookingCard key={booking.id} booking={booking} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cancelled Bookings */}
                  {groupedBookings.cancelled.length > 0 && (
                    <div className="booking-group">
                      <h3 className="group-title">Cancelled Bookings ({groupedBookings.cancelled.length})</h3>
                      <div className="bookings-grid">
                        {groupedBookings.cancelled.map(booking => (
                          <BookingCard key={booking.id} booking={booking} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'help' && (
            <div className="help-section">
              <div className="help-content">
                <h3>Need Help?</h3>
                <div className="help-cards">
                  <div className="help-card">
                    <div className="help-icon">
                      <Phone size={24} />
                    </div>
                    <div className="help-info">
                      <h4>Call Us</h4>
                      <p>For immediate assistance and booking cancellations</p>
                      <a href="tel:+917796775959" className="help-link">+91 779677 5959</a>
                    </div>
                  </div>
                  
                  <div className="help-card">
                    <div className="help-icon">
                      <Mail size={24} />
                    </div>
                    <div className="help-info">
                      <h4>Email Support</h4>
                      <p>Send us your queries and cancellation requests</p>
                      <a href="mailto:thecontainerkop@gmail.com" className="help-link">thecontainerkop@gmail.com</a>
                    </div>
                  </div>
                  
                  <div className="help-card">
                    <div className="help-icon">
                      <MapPin size={24} />
                    </div>
                    <div className="help-info">
                      <h4>Visit Us</h4>
                      <p>Hotel The Container</p>
                      <p className="help-address">Address details coming soon</p>
                    </div>
                  </div>
                </div>

                <div className="cancellation-policy">
                  <h4>Cancellation Policy</h4>
                  <div className="policy-content">
                    <p><strong>For booking cancellations, please contact the hotel directly:</strong></p>
                    <ul>
                      <li>Call us at <strong>+91 99608 10617</strong> during business hours</li>
                      <li>Email us at <strong>thecontainerkop@gmail.com</strong> with your booking ID</li>
                      <li>Cancellation policies may vary based on your booking type and dates</li>
                      <li>Free cancellation may be available up to 24 hours before check-in</li>
                    </ul>
                    <p className="policy-note">
                      <AlertCircle size={16} />
                      Online cancellation feature will be available soon. For now, please contact us directly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function BookingCard({ booking }) {
    const nights = calculateNights(booking.checkInDate, booking.checkOutDate);
    
    return (
      <div className="booking-card">
        <div className="booking-header">
          <div className="booking-id">#{booking.id}</div>
          <div className={getStatusClass(booking.status)}>
            {getStatusIcon(booking.status)}
            <span>{booking.status || 'Unknown'}</span>
          </div>
        </div>
        
        <div className="booking-details">
          <h4 className="room-name">{booking.roomName || 'Room'}</h4>
          
          <div className="booking-info">
            <div className="info-item">
              <Calendar size={16} />
              <span>{formatDateForDisplay(booking.checkInDate)} - {formatDateForDisplay(booking.checkOutDate)}</span>
            </div>
            
            <div className="info-item">
              <Users size={16} />
              <span>{booking.guests} guest{booking.guests > 1 ? 's' : ''} • {nights} night{nights > 1 ? 's' : ''}</span>
            </div>
            
            {booking.totalPrice && (
              <div className="info-item booking-price">
                <span>Total: ₹{booking.totalPrice}</span>
              </div>
            )}
          </div>

          {booking.breakfast && (
            <div className="booking-extras">
              <span className="extra-tag">Breakfast included</span>
            </div>
          )}
          
          {booking.createdAt && (
            <div className="booking-meta">
              <small>Booked on {formatDateForDisplay(booking.createdAt)}</small>
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default Profile;
